import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Segment, Button } from 'semantic-ui-react';
import Currency from 'react-currency-formatter';
import { Bar } from 'react-chartjs-2';
import Datetime from '../datepicker/DateTime';
import { uiActions, historyActions } from '../../actions/index';
import Strings from '../../common/Strings';
import Constant from '../../common/Constant';
import { withDayChange } from '../index';
import './HistoryCalendar.css';
import { exportSingleMonthCsv } from '../../helpers/export';
import Events from '../../analytics/Events';
import Analytics from '../../analytics/Analytics';
import {
  dropSecondsAndMillisFromWorkLogMillis,
  getHoursFromMillis,
  getLoggedTimeFromHours,
  getLoggedTimeFromMillis,
} from '../../helpers/workLog';
import { getProjectWithId } from '../../helpers/project';

const moment = require('moment');

class HistoryCalendar extends React.Component {
  static getMarkedDates(workLogs) {
    const markedDates = new Map();
    workLogs.forEach(w => markedDates.set(w.date, w));
    return markedDates;
  }

  constructor(props) {
    super(props);

    this.state = {
      date: moment(),
      month: moment(),
      renderSelectedDate: true,
    };

    const { dispatch } = this.props;
    dispatch(historyActions.fetchWorkLogs(this.state.month));
    dispatch(
      uiActions.historyDetailsClicked(
        this.state.date.format(Constant.DATE_FORMAT_SHORT),
      ),
    );
  }

  componentWillUnmount() {
    const { dispatch } = this.props;
    dispatch(historyActions.stopSync());
  }

  onDateChange = date => {
    const { dispatch } = this.props;
    if (date.format('YYYY MMM') !== this.state.month.format('YYYY MMM')) {
      this.setState({ date, month: date, renderSelectedDate: true });
      dispatch(historyActions.fetchWorkLogs(date));
    } else {
      this.setState({ date, renderSelectedDate: true });
    }

    dispatch(
      uiActions.historyDetailsClicked(date.format(Constant.DATE_FORMAT_SHORT)),
    );
    dispatch(uiActions.activityAddTimeClicked());

    Analytics.logEvent(Events.CLICK_HISTORY_DATE, {
      date: moment(date).format(Constant.DATE_FORMAT_SHORT),
    });
  };

  onMonthChange = month => {
    this.setState({ month, date: month, renderSelectedDate: false });

    const { dispatch } = this.props;
    dispatch(historyActions.fetchWorkLogs(month));
    dispatch(uiActions.historyDetailsClicked(undefined));

    Analytics.logEvent(Events.CLICK_HISTORY_MONTH, {
      date: month.format(Constant.DATE_FORMAT_SHORT),
    });
  };

  onGraphBarClicked = e => {
    if (e.length === 1) {
      const date = moment(e[0]._model.label);
      this.onDateChange(date);
    }
  };

  onExportClicked = () => {
    exportSingleMonthCsv(
      this.state.month,
      this.filterCurrentMonthWorkLogs(this.props.workLogs),
    );
  };

  getHoursLoggedText() {
    let millis = 0;
    this.filterCurrentMonthWorkLogs(this.props.workLogs).forEach(workLog => {
      millis += dropSecondsAndMillisFromWorkLogMillis(workLog.totalLoggedMillis);
    });

    return getLoggedTimeFromMillis(millis);
  }

  getMonthText() {
    return this.state.month.format('MMMM');
  }

  getNumberOfProjectsText() {
    const projects = new Map();
    this.filterCurrentMonthWorkLogs(this.props.workLogs).forEach(workLog => {
      const id = workLog.projectId;
      if (!projects.has(id)) {
        projects.set(id, id);
      }
    });

    return projects.size;
  }

  filterCurrentMonthWorkLogs(workLogs) {
    const filteredWorkLogs = [];
    workLogs.forEach(w => {
      if (
        this.state.month.format('YYYY-MM') === moment(w.date).format('YYYY-MM')
      ) {
        filteredWorkLogs.push(w);
      }
    });
    return filteredWorkLogs;
  }

  isMarked = date => {
    const { markedDates } = this.props;
    if (markedDates) {
      return markedDates.has(moment(date).format(Constant.DATE_FORMAT_SHORT));
    }
    return false;
  };

  renderEarnings() {
    const earnings = new Map();
    this.filterCurrentMonthWorkLogs(this.props.workLogs).forEach(workLog => {
      const { currencyCode, hourlyRate } = getProjectWithId(workLog.projectId);
      if (!earnings.has(currencyCode)) {
        earnings.set(currencyCode, 0.0);
      }
      const millisToUse = dropSecondsAndMillisFromWorkLogMillis(
        workLog.totalLoggedMillis,
      );
      const hoursToUse = getHoursFromMillis(millisToUse);
      const amount = (hourlyRate * hoursToUse).toFixed(2);
      const rate = parseFloat(amount);
      const newAmount = earnings.get(currencyCode) + rate;
      earnings.set(currencyCode, newAmount);
    });

    const rows = [];
    if (earnings.size === 0 && this.props.defaultCurrencyCode) {
      earnings.set(this.props.defaultCurrencyCode, 0.0);
    }
    earnings.forEach((value, key) => {
      rows.push(
        <p className="history-calendar-stats-value">
          <Currency quantity={value} currency={key} />
        </p>,
      );
    });

    return rows;
  }

  renderGraph() {
    const labels = [];
    const hours = [];
    let { workLogs } = this.props;
    workLogs = this.filterCurrentMonthWorkLogs(workLogs);

    if (workLogs.length > 0) {
      let startDate = moment(workLogs[0].date).startOf('month');
      const endDate = moment(workLogs[0].date).endOf('month');
      const workLogMap = new Map();
      workLogs.forEach(w => {
        const existingHours = workLogMap.get(w.date);
        if (existingHours) {
          workLogMap.set(
            w.date,
            existingHours + getHoursFromMillis(w.totalLoggedMillis, true),
          );
        } else {
          workLogMap.set(w.date, getHoursFromMillis(w.totalLoggedMillis, true));
        }
      });

      while (endDate.diff(startDate, 'days') >= 0) {
        labels.push(startDate.format('MMM D, YYYY'));

        const workLogHours = workLogMap.get(
          startDate.format(Constant.DATE_FORMAT_SHORT),
        );
        if (workLogHours) {
          hours.push(workLogHours);
        } else {
          hours.push(0);
        }

        startDate = startDate.add(1, 'days');
      }
    }

    const barData = {
      labels,
      datasets: [
        {
          backgroundColor: '#4588D5',
          data: hours,
        },
      ],
    };

    return (
      <div id="history-calendar-graph">
        <Bar
          getElementAtEvent={this.onGraphBarClicked}
          data={barData}
          width={350}
          height={160}
          options={{
            maintainAspectRatio: false,
            legend: {
              display: false,
            },
            title: {
              display: false,
            },
            tooltips: {
              titleFontFamily: 'Arial',
              titleFontSize: 11,
              titleFontStyle: 'normal',
              titleMarginBottom: 8,
              bodyFontFamily: 'Arial',
              bodyFontSize: 11,
              bodyFontStyle: 'bold',
              displayColors: false,
              callbacks: {
                label: tooltipItem =>
                  getLoggedTimeFromHours(tooltipItem.yLabel),
              },
            },
            scales: {
              xAxes: [
                {
                  barThickness: 8,
                  ticks: {
                    display: false,
                  },
                  gridLines: {
                    display: false,
                  },
                },
              ],
              yAxes: [
                {
                  ticks: {
                    suggestedMax: 8,
                    stepSize: 1,
                    beginAtZero: true,
                    display: false,
                  },
                },
              ],
            },
          }}
        />
      </div>
    );
  }

  render() {
    return (
      <div className="history-calendar-root">
        <Segment basic className="history-calendar-container">
          <h1 id="history-calendar-title">{Strings.HISTORY.title}</h1>
          <div className="history-calendar-divider" />
          <Datetime
            input={false}
            value={this.state.date}
            onChange={this.onDateChange}
            onMonthChange={this.onMonthChange}
            isMarkedDate={this.isMarked}
            renderSelectedDate={this.state.renderSelectedDate}
          />
          <div className="history-calendar-divider" />
          <div className="history-calendar-stats-header">
            <p id="history-calendar-stats">{Strings.HISTORY.stats}</p>
            <p id="history-calendar-stats-month">{this.getMonthText()}</p>
            <div className="history-calendar-title-space" />
            <Button
              id="history-calendar-export-button"
              onClick={this.onExportClicked}
              disabled={
                this.filterCurrentMonthWorkLogs(this.props.workLogs).length ===
                0
              }
            >
              {Strings.HISTORY.export}
            </Button>
          </div>
          <div className="history-calendar-stats-container">
            <p id="hours-logged-label" className="history-calendar-stats-label">
              {Strings.HISTORY.timeLogged}
            </p>
            <p id="hours-logged-value" className="history-calendar-stats-value">
              {this.getHoursLoggedText()}
            </p>
          </div>
          <div className="history-calendar-stats-container">
            <p
              id="number-of-projects-label"
              className="history-calendar-stats-label"
            >
              {Strings.HISTORY.numberOfProjects}
            </p>
            <p
              id="number-of-projects-value"
              className="history-calendar-stats-value"
            >
              {this.getNumberOfProjectsText()}
            </p>
          </div>
          <div className="history-calendar-stats-container">
            <p id="earnings-label" className="history-calendar-stats-label">
              {Strings.HISTORY.earnings}
            </p>
            <div>{this.renderEarnings()}</div>
          </div>
          {this.renderGraph()}
        </Segment>
      </div>
    );
  }
}

HistoryCalendar.propTypes = {
  dispatch: PropTypes.func.isRequired,
  workLogs: PropTypes.arrayOf(
    PropTypes.shape({
      totalLoggedMillis: PropTypes.number.isRequired,
      project: PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        color: PropTypes.string.isRequired,
        currencyCode: PropTypes.string.isRequired,
      }).isRequired,
    }),
  ),
  markedDates: PropTypes.shape,
  defaultCurrencyCode: PropTypes.string.isRequired,
  projects: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      color: PropTypes.string.isRequired,
    }),
  ),
};

HistoryCalendar.defaultProps = {
  workLogs: [],
  markedDates: undefined,
  projects: [],
};

const mapStateToProps = state => ({
  workLogs: state.history,
  markedDates: HistoryCalendar.getMarkedDates(state.history),
  defaultCurrencyCode: state.user.settings.defaultCurrency,
  projects: state.projects,
});

export default connect(mapStateToProps)(withDayChange(HistoryCalendar));



// WEBPACK FOOTER //
// ./src/components/history/HistoryCalendar.js